import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SearchIcon from '@mui/icons-material/Search';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ExerciseEditorDialog from './../exercises/ExerciseEditorDialog';
import WorkoutExerciseSearch from './WorkoutExerciseSearch';
import Loading from '../shared/Loading';
import ServerError from '../error/ServerError';
import { mongoObjectId } from '../../utils/mongoObjectId';
import { SEARCH_EXERCISES } from '../../graphql/exercise';
import { useLazyQuery } from '@apollo/client/react';

const groupAccent = (index) => {
  if (index === 1) return '#6254e8';
  if (index === 2) return '#f97316';
  return '#4338ca';
};

const exerciseImage = (exercise, size = 64) => {
  const url = exercise?.images?.[0]?.url;
  if (!url) {
    return (
      <Box
        sx={{
          width: size,
          height: size * 0.72,
          borderRadius: 1,
          backgroundColor: '#101820',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FitnessCenterIcon fontSize="small" />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={url}
      alt={exercise.title}
      sx={{
        width: size,
        height: size * 0.72,
        borderRadius: 1,
        objectFit: 'cover',
        backgroundColor: '#101820',
      }}
    />
  );
};

const exerciseCount = (groups) =>
  (groups ?? []).reduce(
    (total, group) => total + (group.exercises?.length ?? 0),
    0
  );

const estimatedVolume = (groups) =>
  (groups ?? []).reduce(
    (total, group) =>
      total +
      (group.exercises ?? []).reduce(
        (sum, exercise) =>
          sum +
          (exercise.sets ?? 1) * (exercise.reps ?? 1) * (exercise.weight || 0),
        0
      ),
    0
  );

const formatExercisePrescription = (exercise, groupIndex) => {
  if (groupIndex >= 2) {
    return exercise.reps
      ? `${exercise.reps} reps`
      : `${exercise.duration ?? 10} sec`;
  }
  if ((exercise.sets ?? 1) > 1) {
    return `${exercise.sets} sets × ${exercise.reps || '10-12'} reps`;
  }
  return exercise.reps
    ? `${exercise.reps} reps`
    : `${exercise.duration ?? 10} sec`;
};

const WorkoutStat = ({ icon, value, label, children }) => (
  <Box
    sx={{
      px: 3,
      py: 2,
      flex: 1,
      minWidth: 180,
      borderRight: '1px solid #dbe3ee',
      '&:last-of-type': { borderRight: 0 },
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      {icon}
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: 24, lineHeight: 1.15 }}>
          {value}
        </Typography>
        <Typography sx={{ color: '#40536b', fontSize: 13 }}>{label}</Typography>
      </Box>
      {children}
    </Stack>
  </Box>
);

const parseInteger = (value, min = 0) => {
  if (value === '') {
    return null;
  }

  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return Math.max(min, parsed);
};

const ExerciseRow = ({
  exercise,
  groupIndex,
  onDurationChange,
  onRestAfterExerciseDurationChange,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{
      px: 2,
      py: 1.25,
      borderTop: '1px solid #dbe3ee',
      flexWrap: { xs: 'wrap', md: 'nowrap' },
    }}
  >
    <DragIndicatorIcon sx={{ color: '#8090a3', fontSize: 20 }} />
    {exerciseImage(exercise)}
    <Typography sx={{ fontWeight: 800, minWidth: 220, flex: 1 }}>
      {exercise.title}
    </Typography>
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{ minWidth: 150 }}
    >
      <FitnessCenterIcon sx={{ color: '#65758b', fontSize: 18 }} />
      <Typography sx={{ color: '#40536b', fontSize: 14 }}>
        {formatExercisePrescription(exercise, groupIndex)}
      </Typography>
    </Stack>
    <TextField
      size="small"
      type="number"
      label="Duration"
      value={exercise.duration ?? ''}
      onChange={(event) =>
        onDurationChange(parseInteger(event.target.value, 1))
      }
      InputProps={{
        inputProps: { min: 1, max: 1000 },
        endAdornment: <InputAdornment position="end">sec</InputAdornment>,
      }}
      sx={{ width: 132 }}
    />
    {groupIndex === 0 && (
      <Typography sx={{ color: '#40536b', fontSize: 14, minWidth: 110 }}>
        {exercise.percentOf1RM ? `${exercise.percentOf1RM}% 1RM` : ''}
      </Typography>
    )}
    <TextField
      size="small"
      type="number"
      label="Rest"
      value={exercise.restAfterExerciseDuration ?? ''}
      onChange={(event) =>
        onRestAfterExerciseDurationChange(parseInteger(event.target.value, 0))
      }
      InputProps={{
        inputProps: { min: 0, max: 1000 },
        startAdornment: (
          <InputAdornment position="start">
            <TimerOutlinedIcon sx={{ color: '#65758b', fontSize: 18 }} />
          </InputAdornment>
        ),
        endAdornment: <InputAdornment position="end">sec</InputAdornment>,
      }}
      sx={{ width: 132 }}
    />
  </Stack>
);

const ExerciseBlock = ({
  group,
  index,
  onAddExercise,
  onRestBetweenRoundsChange,
  onExerciseDurationChange,
  onExerciseRestAfterDurationChange,
}) => {
  const accent = groupAccent(index);
  const isPlainBlock = index === 0 && !group.groupName;
  const title = isPlainBlock
    ? 'Exercises'
    : group.groupName || (index === 1 ? 'Superset A' : 'Finisher');

  return (
    <Card variant="outlined" sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, py: 1.5 }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            {!isPlainBlock && (
              <>
                <DragIndicatorIcon sx={{ color: accent, fontSize: 19 }} />
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 2,
                    backgroundColor: accent,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ViewModuleIcon sx={{ fontSize: 17 }} />
                </Box>
              </>
            )}
            <Typography sx={{ fontWeight: 800, fontSize: 20, color: accent }}>
              {title}
            </Typography>
            {!isPlainBlock && (
              <Chip
                size="small"
                label={`${group.exercises?.length ?? 0} Exercises`}
                sx={{
                  backgroundColor: `${accent}1a`,
                  color: accent,
                  fontWeight: 700,
                }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={3} alignItems="center">
            {!isPlainBlock && (
              <>
                <Typography sx={{ color: '#40536b', fontWeight: 700 }}>
                  {group.rounds ?? 1} Rounds
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  label="Superset rest"
                  value={group.restBetweenRounds ?? 0}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) =>
                    onRestBetweenRoundsChange(
                      group._id,
                      parseInteger(event.target.value, 0)
                    )
                  }
                  InputProps={{
                    inputProps: { min: 0, max: 1000 },
                    endAdornment: (
                      <InputAdornment position="end">sec</InputAdornment>
                    ),
                  }}
                  sx={{ width: 156 }}
                />
              </>
            )}
            {isPlainBlock && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={(event) => {
                  event.stopPropagation();
                  onAddExercise();
                }}
              >
                Add Exercise
              </Button>
            )}
          </Stack>
        </Stack>
        {(group.exercises ?? []).map((exercise, exerciseIndex) => (
          <ExerciseRow
            key={exercise._id}
            exercise={exercise}
            groupIndex={index}
            onDurationChange={(duration) =>
              onExerciseDurationChange(group._id, exerciseIndex, duration)
            }
            onRestAfterExerciseDurationChange={(duration) =>
              onExerciseRestAfterDurationChange(
                group._id,
                exerciseIndex,
                duration
              )
            }
          />
        ))}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={(event) => {
            event.stopPropagation();
            onAddExercise();
          }}
          sx={{
            borderRadius: 0,
            borderStyle: 'dashed',
            borderLeft: 0,
            borderRight: 0,
            borderBottom: 0,
            py: 1.2,
          }}
        >
          Add Exercise
        </Button>
      </CardContent>
    </Card>
  );
};

const ExerciseLibrary = ({ onSelect, onAddNew, createdExercises = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [search, { loading, error, data }] = useLazyQuery(SEARCH_EXERCISES);
  const delayedSearch = useCallback(_.debounce(search, 500), [search]);

  useEffect(() => {
    delayedSearch({
      variables: {
        input: {
          searchTerm: searchQuery,
          limit: 8,
        },
      },
    });
  }, [delayedSearch, searchQuery]);

  const exercises = [
    ...createdExercises,
    ...(data?.exercises ?? []).filter(
      (exercise) =>
        !createdExercises.some((created) => created._id === exercise._id)
    ),
  ];

  return (
    <Card variant="outlined" sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
            Exercise Library
          </Typography>
          <Button size="small">View all</Button>
        </Stack>
        <TextField
          fullWidth
          size="small"
          placeholder="Search exercises..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#65758b', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />
        <ToggleButtonGroup value="all" exclusive size="small" sx={{ mb: 2 }}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="recent">Recent</ToggleButton>
          <ToggleButton value="favorites">Favorites</ToggleButton>
        </ToggleButtonGroup>
        {!!loading && <Loading circular />}
        {!!error && <ServerError message={error.message} />}
        <Stack spacing={1}>
          {exercises.map((exercise) => (
            <Stack
              key={exercise._id}
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ pb: 1, borderBottom: '1px solid #edf1f6' }}
            >
              <DragIndicatorIcon sx={{ color: '#8090a3', fontSize: 18 }} />
              {exerciseImage(exercise, 42)}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontWeight: 800, fontSize: 14 }}>
                  {exercise.title}
                </Typography>
                <Typography noWrap sx={{ color: '#40536b', fontSize: 13 }}>
                  {exercise.equipment || exercise.tags?.[0] || 'Exercise'}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => onSelect(exercise)}
                sx={{ border: '1px solid #d0d9e6', borderRadius: 1 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Button startIcon={<AddIcon />} onClick={onAddNew} sx={{ mt: 1.5 }}>
          Create New Exercise
        </Button>
      </CardContent>
    </Card>
  );
};

const WorkoutContent = ({ workout, setWorkout }) => {
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [addExerciseOpen, setAddExerciseOpen] = useState(false);
  const [openExerciseCreator, setOpenExerciseCreator] = useState(false);
  const [createdExercises, setCreatedExercises] = useState([]);

  const groups = useMemo(() => workout?.exerciseGroups ?? [], [workout]);

  useEffect(() => {
    if (!selectedGroupId && groups[0]?._id) {
      setSelectedGroupId(groups[0]._id);
    }
  }, [groups, selectedGroupId]);

  if (!workout) {
    return <Loading />;
  }

  const ensureGroup = () => {
    if (workout.exerciseGroups?.length) {
      return workout.exerciseGroups;
    }

    return [
      {
        _id: mongoObjectId(),
        groupName: '',
        rounds: 1,
        restBetweenRounds: 0,
        exercises: [],
      },
    ];
  };

  const handleAddGroup = (template = {}) => {
    const newGroup = {
      _id: mongoObjectId(),
      groupName: template.groupName ?? '',
      rounds: template.rounds ?? 1,
      restBetweenRounds: template.restBetweenRounds ?? 0,
      exercises: [],
    };
    const exerciseGroups = [...ensureGroup(), newGroup];
    setSelectedGroupId(newGroup._id);
    setWorkout({ ...workout, exerciseGroups });
  };

  const handleAddExerciseOpen = (group) => {
    setSelectedGroupId(group._id);
    setAddExerciseOpen(true);
  };

  const handleAddExerciseClose = () => {
    setAddExerciseOpen(false);
  };

  const handleCreateExerciseOpen = () => {
    setAddExerciseOpen(false);
    setOpenExerciseCreator(true);
  };

  const addExerciseToSelectedGroup = (exercise) => {
    const copy = _.cloneDeep({
      ...exercise,
      duration: 10,
      reps: 10,
      sets: 1,
      percentOf1RM: 0,
      weight: 0,
      weightUnit: 'lbs',
      restBetweenSetsDuration: 0,
      restAfterExerciseDuration: 0,
      isTimed: false,
    });
    copy.exerciseId = exercise._id;
    copy._id = mongoObjectId();

    const existingGroups = ensureGroup();
    const targetGroupId = selectedGroupId ?? existingGroups[0]._id;
    const exerciseGroups = existingGroups.map((group) => {
      if (group._id !== targetGroupId) return group;
      return {
        ...group,
        exercises: [...(group.exercises ?? []), copy],
      };
    });
    setWorkout({ ...workout, exerciseGroups, autoSave: false });
  };

  const handleDrawerExerciseSelected = (exercise) => {
    addExerciseToSelectedGroup(exercise);
    handleAddExerciseClose();
  };

  const handleExerciseCreated = (exercise) => {
    setOpenExerciseCreator(false);
    if (!exercise?._id) {
      return;
    }
    setCreatedExercises((current) => [
      exercise,
      ...current.filter((item) => item._id !== exercise._id),
    ]);
  };

  const handleExerciseDurationChange = (groupId, exerciseIndex, duration) => {
    const exerciseGroups = ensureGroup().map((group) => {
      if (group._id !== groupId) {
        return group;
      }

      return {
        ...group,
        exercises: (group.exercises ?? []).map((exercise, index) => {
          if (index !== exerciseIndex) {
            return exercise;
          }

          return {
            ...exercise,
            duration,
            isTimed: duration != null,
          };
        }),
      };
    });

    setWorkout({ ...workout, exerciseGroups, autoSave: false });
  };

  const handleExerciseRestAfterDurationChange = (
    groupId,
    exerciseIndex,
    duration
  ) => {
    const exerciseGroups = ensureGroup().map((group) => {
      if (group._id !== groupId) {
        return group;
      }

      return {
        ...group,
        exercises: (group.exercises ?? []).map((exercise, index) => {
          if (index !== exerciseIndex) {
            return exercise;
          }

          return {
            ...exercise,
            restAfterExerciseDuration: duration,
          };
        }),
      };
    });

    setWorkout({ ...workout, exerciseGroups, autoSave: false });
  };

  const handleRestBetweenRoundsChange = (groupId, duration) => {
    const exerciseGroups = ensureGroup().map((group) => {
      if (group._id !== groupId) {
        return group;
      }

      return {
        ...group,
        restBetweenRounds: duration ?? 0,
      };
    });

    setWorkout({ ...workout, exerciseGroups, autoSave: false });
  };

  const duration = Math.max(1, Math.floor((workout.durationTotal ?? 0) / 60));
  const count = exerciseCount(groups);
  const volume = estimatedVolume(groups).toLocaleString();

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={9}>
          <Stack spacing={2}>
            <Card
              variant="outlined"
              sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
            >
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Stack direction="row" flexWrap="wrap">
                  <WorkoutStat
                    icon={<ScheduleIcon sx={{ color: '#4338ca' }} />}
                    value={duration}
                    label="Est. Duration"
                  />
                  <WorkoutStat
                    icon={<FitnessCenterIcon sx={{ color: '#4338ca' }} />}
                    value={count}
                    label="Exercises"
                  />
                  <WorkoutStat
                    icon={<ViewModuleIcon sx={{ color: '#4338ca' }} />}
                    value={volume}
                    label="Est. Volume"
                  />
                  <WorkoutStat value="Muscle Groups" label="">
                    <Stack direction="row" spacing={0.75} sx={{ ml: 1 }}>
                      {['Chest', 'Back', 'Shoulders', '+2'].map((label) => (
                        <Chip
                          key={label}
                          size="small"
                          label={label}
                          sx={{ fontWeight: 700 }}
                        />
                      ))}
                    </Stack>
                  </WorkoutStat>
                </Stack>
              </CardContent>
            </Card>

            {groups.length === 0 && (
              <Card
                variant="outlined"
                sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
              >
                <CardContent>
                  <Typography sx={{ fontWeight: 800, mb: 1 }}>
                    Exercises
                  </Typography>
                  <Typography sx={{ color: '#40536b', mb: 2 }}>
                    Add a block, then choose exercises from the library.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddGroup()}
                  >
                    Add Block
                  </Button>
                </CardContent>
              </Card>
            )}

            {groups.map((group, index) => (
              <Box
                key={group._id}
                onClick={() => setSelectedGroupId(group._id)}
              >
                <ExerciseBlock
                  group={group}
                  index={index}
                  onAddExercise={() => handleAddExerciseOpen(group)}
                  onRestBetweenRoundsChange={handleRestBetweenRoundsChange}
                  onExerciseDurationChange={handleExerciseDurationChange}
                  onExerciseRestAfterDurationChange={
                    handleExerciseRestAfterDurationChange
                  }
                />
              </Box>
            ))}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleAddGroup({ groupName: '' })}
              >
                Add Block
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ViewModuleIcon />}
                onClick={() =>
                  handleAddGroup({
                    groupName: 'Superset',
                    rounds: 3,
                    restBetweenRounds: 0,
                  })
                }
              >
                Add Superset / Circuit
              </Button>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={3}>
          <Stack spacing={2}>
            <ExerciseLibrary
              onSelect={addExerciseToSelectedGroup}
              onAddNew={handleCreateExerciseOpen}
              createdExercises={createdExercises}
            />
            <Card
              variant="outlined"
              sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
            >
              <CardContent>
                <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 1.5 }}>
                  Workout Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  placeholder="Add notes about this workout..."
                  value={workout.desc ?? ''}
                  onChange={(event) =>
                    setWorkout({ ...workout, desc: event.target.value })
                  }
                  inputProps={{ maxLength: 500 }}
                  helperText={`${workout.desc?.length ?? 0} / 500`}
                />
              </CardContent>
            </Card>
            <Card
              variant="outlined"
              sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
            >
              <CardContent>
                <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 1 }}>
                  Workout Settings
                </Typography>
                {[
                  ['Rest Timer', 'Show rest timer to athletes'],
                  ['Exercise Numbering', 'Show numbers to athletes'],
                ].map(([title, subtitle]) => (
                  <Stack
                    key={title}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ py: 1.25 }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
                      <Typography sx={{ color: '#40536b', fontSize: 13 }}>
                        {subtitle}
                      </Typography>
                    </Box>
                    <Switch defaultChecked color="primary" />
                  </Stack>
                ))}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <ExerciseEditorDialog
        key="new"
        onSaved={handleExerciseCreated}
        open={openExerciseCreator}
        onClose={() => setOpenExerciseCreator(false)}
        exercise={{}}
      />
      <Drawer
        sx={{ zIndex: 2000 }}
        anchor="bottom"
        open={addExerciseOpen}
        onClose={handleAddExerciseClose}
      >
        <Box p={3}>
          <WorkoutExerciseSearch
            onSelect={handleDrawerExerciseSelected}
            onAddNew={handleCreateExerciseOpen}
            onClose={handleAddExerciseClose}
          />
        </Box>
      </Drawer>
    </Box>
  );
};

export default WorkoutContent;
